import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Edit2,
  Filter,
  KeyRound,
  MoreHorizontal,
  PlusCircle,
  Search,
  UserCheck,
  Users,
  UserX,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import AddReferringDentistDialog from '@/components/AddReferringDentistDialog';
import ReferringDoctorPatientsDialog from '@/components/ReferringDoctorPatientsDialog';
import {
  createReferringClinic,
  createReferringPortalAccount,
  listReferringClinics,
  listReferringDoctors,
  resetReferringPortalPassword,
  toggleReferringDoctorActive,
  type ReferringClinic,
  type ReferringDoctorWithStats,
} from '@/services/referringDirectoryService';

// Readable temporary password for a new referring-dentist portal login.
const generateTempPassword = (): string => {
  const adjectives = ['Bright', 'Swift', 'Calm', 'Bold', 'Clear', 'Prime'];
  const nouns = ['Tooth', 'Molar', 'Smile', 'Crown', 'Root', 'Enamel'];
  const pick = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];
  const digits = String(Math.floor(1000 + Math.random() * 9000));
  return `${pick(adjectives)}${pick(nouns)}${digits}`;
};

const PAGE_SIZE = 50;

interface ClinicFormState {
  name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
}

const EMPTY_CLINIC_FORM: ClinicFormState = {
  name: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
};

export interface ReferringDentistsTableProps {
  /**
   * Whether the query should run. Lets callers gate fetch on access (e.g. the
   * standalone admin page only fetches when canAccessAdminPanel() is true). The
   * embedded variant inside the Patients view passes `true` so all staff who
   * can see the Patients view also see the directory.
   */
  enabled?: boolean;
  /**
   * `embedded` (default) renders just the directory card so it slots cleanly
   * inside an existing page (e.g. a tab inside PatientsView). `admin` adds the
   * page-level title row + action buttons, used by the standalone
   * /admin/referring-dentists page.
   */
  variant?: 'admin' | 'embedded';
  /**
   * Optional slot rendered next to the directory title (right side of the
   * card header). Used by the embedded variant to put the Add/Edit buttons
   * inside the card instead of relying on a separate page header.
   */
  headerActions?: React.ReactNode;
}

/**
 * Shared table + dialogs for the referring-dentist directory.
 *
 * Used by both the standalone `/admin/referring-dentists` page and the
 * `Referring Dentists` tab inside PatientsView. Owns all data fetching,
 * filters, pagination, and the add-clinic/add-dentist dialogs so there is no
 * duplicated logic between the two surfaces.
 */
const ReferringDentistsTable = ({
  enabled = true,
  variant = 'embedded',
  headerActions,
}: ReferringDentistsTableProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [clinicFilter, setClinicFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [portalFilter, setPortalFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'referrals' | 'recent'>('name');
  const [page, setPage] = useState(1);

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    editing: ReferringDoctorWithStats | null;
  }>({ open: false, editing: null });

  const [clinicDialogOpen, setClinicDialogOpen] = useState(false);
  const [clinicForm, setClinicForm] = useState<ClinicFormState>(EMPTY_CLINIC_FORM);
  const [patientsDialog, setPatientsDialog] = useState<{
    open: boolean;
    doctor: ReferringDoctorWithStats | null;
  }>({ open: false, doctor: null });

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const filters = useMemo(
    () => ({
      search: debouncedSearch.replace(/[(),]/g, ''),
      clinicId: clinicFilter,
      activeStatus: activeFilter,
      hasPortalAccount: portalFilter,
    }),
    [debouncedSearch, clinicFilter, activeFilter, portalFilter],
  );

  const doctorsQuery = useQuery({
    queryKey: ['referring_doctors', filters],
    queryFn: () => listReferringDoctors(filters),
    enabled,
  });

  const clinicsQuery = useQuery({
    queryKey: ['referring_clinics', 'all'],
    queryFn: () => listReferringClinics(),
    enabled,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      toggleReferringDoctorActive(id, active),
    onSuccess: async (doctor) => {
      await queryClient.invalidateQueries({ queryKey: ['referring_doctors'] });
      toast({
        title: doctor.is_active ? 'Doctor reactivated' : 'Doctor deactivated',
        description: doctor.name,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Could not update doctor',
        description:
          error instanceof Error ? error.message : 'Unknown error toggling status.',
        variant: 'destructive',
      });
    },
  });

  // --- Create portal account for a referring dentist -----------------------
  const [accountDialog, setAccountDialog] = useState<{
    open: boolean;
    doctor: ReferringDoctorWithStats | null;
  }>({ open: false, doctor: null });
  const [accountEmail, setAccountEmail] = useState('');
  const [accountPassword, setAccountPassword] = useState('');

  const openAccountDialog = (doctor: ReferringDoctorWithStats) => {
    setAccountEmail(doctor.email ?? '');
    setAccountPassword(generateTempPassword());
    setAccountDialog({ open: true, doctor });
  };

  const createAccountMutation = useMutation({
    mutationFn: ({
      doctor,
      email,
      password,
    }: {
      doctor: ReferringDoctorWithStats;
      email: string;
      password: string;
    }) =>
      createReferringPortalAccount({
        email,
        password,
        name: doctor.name,
        doctorId: doctor.id,
        clinicId: doctor.clinic_id ?? null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['referring_doctors'] });
      toast({
        title: 'Portal account created',
        description: 'Share the email + temporary password with the dentist.',
      });
      setAccountDialog({ open: false, doctor: null });
    },
    onError: (error: unknown) => {
      const err = error as { code?: string; message?: string } | null;
      let description = 'Unknown error creating the account.';
      if (err?.code === '23505') {
        description =
          'An account with this email already exists. Use a different email, or this dentist may already have a login.';
      } else if (err?.code === '23514') {
        description = 'Please enter a valid email address for the login.';
      } else if (err?.message) {
        description = err.message;
      }
      toast({
        title: 'Could not create portal account',
        description,
        variant: 'destructive',
      });
    },
  });

  // --- Reset portal password for a dentist who already has a login ---------
  // Same confirm-dialog + temp-password flow as the edit dialog, surfaced from
  // the row's actions menu so the front desk doesn't have to open Edit first.
  const [resetDialog, setResetDialog] = useState<{
    open: boolean;
    doctor: ReferringDoctorWithStats | null;
  }>({ open: false, doctor: null });
  const [resetPassword, setResetPassword] = useState('');

  const openResetDialog = (doctor: ReferringDoctorWithStats) => {
    setResetPassword(generateTempPassword());
    setResetDialog({ open: true, doctor });
  };

  const resetPasswordMutation = useMutation({
    mutationFn: ({ accountId, password }: { accountId: string; password: string }) =>
      resetReferringPortalPassword(accountId, password),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['referring_doctors'] });
      toast({
        title: 'Password reset',
        description: `Share the new temporary password with ${
          resetDialog.doctor?.name ?? 'the dentist'
        }.`,
      });
      setResetDialog({ open: false, doctor: null });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Could not reset password',
        description: error instanceof Error ? error.message : 'Unknown error.',
        variant: 'destructive',
      });
    },
  });

  const createClinicMutation = useMutation({
    mutationFn: createReferringClinic,
    onSuccess: async (clinic) => {
      await queryClient.invalidateQueries({ queryKey: ['referring_clinics'] });
      toast({
        title: 'Clinic added',
        description: `${clinic.name} is now in the clinic directory.`,
      });
      setClinicForm(EMPTY_CLINIC_FORM);
      setClinicDialogOpen(false);
    },
    onError: (error: unknown) => {
      toast({
        title: 'Could not add clinic',
        description:
          error instanceof Error ? error.message : 'Unknown error creating clinic.',
        variant: 'destructive',
      });
    },
  });

  const allDoctors = useMemo(() => {
    const rows = [...(doctorsQuery.data ?? [])];
    const referralCount = (d: ReferringDoctorWithStats) =>
      d.plato_patient_count || d.total_referrals || 0;
    switch (sortBy) {
      case 'referrals':
        rows.sort((a, b) => referralCount(b) - referralCount(a) || a.name.localeCompare(b.name));
        break;
      case 'recent':
        rows.sort((a, b) => {
          const av = a.last_referral_date ?? '';
          const bv = b.last_referral_date ?? '';
          if (av === bv) return a.name.localeCompare(b.name);
          return bv.localeCompare(av); // most recent first; empties sink
        });
        break;
      default:
        rows.sort((a, b) => a.name.localeCompare(b.name));
    }
    return rows;
  }, [doctorsQuery.data, sortBy]);
  const totalRecords = allDoctors.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedDoctors = useMemo(
    () => allDoctors.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [allDoctors, safePage],
  );

  const clinics: ReferringClinic[] = clinicsQuery.data ?? [];

  const handleSubmitClinic = (event: React.FormEvent) => {
    event.preventDefault();
    if (clinicForm.name.trim() === '') {
      toast({
        title: 'Clinic name required',
        description: 'Enter a clinic name to continue.',
        variant: 'destructive',
      });
      return;
    }
    createClinicMutation.mutate({
      name: clinicForm.name,
      email: clinicForm.email,
      phone: clinicForm.phone,
      address: clinicForm.address,
      notes: clinicForm.notes,
    });
  };

  const formatLastReferral = (date: string | null) => {
    if (!date) return 'Never';
    try {
      return format(new Date(date), 'MMM d, yyyy');
    } catch {
      return date;
    }
  };

  const formatLastLogin = (date: string | null) => {
    if (!date) return 'Never';
    try {
      return format(new Date(date), 'MMM d, yyyy');
    } catch {
      return date;
    }
  };

  /**
   * One truthful portal-status chip per row, mapped from the linked
   * external_referral_accounts record:
   *   - no linked account            -> "No portal" (muted)
   *   - account.is_active === false  -> "Disabled" (muted)
   *   - password_reset_required      -> "Reset pending" (amber): a temporary
   *     password was issued and the dentist must set a new one at next sign-in
   *   - otherwise                    -> "Active" (green) + last sign-in date
   */
  const renderPortalStatus = (doctor: ReferringDoctorWithStats) => {
    const account = doctor.portal_account;
    if (!account) {
      return (
        <Badge variant="outline" className="font-normal text-slate-400 border-slate-200">
          No portal
        </Badge>
      );
    }
    if (account.is_active === false) {
      return (
        <Badge
          variant="secondary"
          className="bg-slate-200 text-slate-600 hover:bg-slate-200"
          title={`Portal login disabled (${account.email})`}
        >
          Disabled
        </Badge>
      );
    }
    if (account.password_reset_required) {
      return (
        <Badge
          className="bg-amber-100 text-amber-800 hover:bg-amber-100"
          title={`Temporary password issued for ${account.email} — the dentist must set a new password at next sign-in`}
        >
          Reset pending
        </Badge>
      );
    }
    return (
      <div className="space-y-0.5">
        <Badge
          className="bg-green-100 text-green-800 hover:bg-green-100"
          title={account.email ?? undefined}
        >
          Active
        </Badge>
        <div className="text-xs text-slate-500">
          {account.last_login_at
            ? `Last sign-in: ${formatLastLogin(account.last_login_at)}`
            : 'Never signed in'}
        </div>
      </div>
    );
  };

  // Action buttons (Add clinic / Add dentist). When the parent provides
  // `headerActions`, the parent decides where they go. When it doesn't, the
  // embedded card renders them inline in the card header.
  const inlineActionButtons = (
    <div className="flex flex-col sm:flex-row gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setClinicForm(EMPTY_CLINIC_FORM);
          setClinicDialogOpen(true);
        }}
      >
        <PlusCircle className="h-4 w-4 mr-1.5" />
        Add clinic
      </Button>
      <Button
        size="sm"
        onClick={() => setDialogState({ open: true, editing: null })}
      >
        <PlusCircle className="h-4 w-4 mr-1.5" />
        Add dentist
      </Button>
    </div>
  );

  return (
    <div className={variant === 'admin' ? 'space-y-4 sm:space-y-6' : 'space-y-4'}>
      {variant === 'admin' ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Referring Dentists</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage the directory of external referring dentists and their clinics.
              {' '}
              {totalRecords > 0 ? (
                <span>{totalRecords} record{totalRecords === 1 ? '' : 's'} match filter.</span>
              ) : null}
            </p>
          </div>
          {inlineActionButtons}
        </div>
      ) : null}

      <Card>
        <CardHeader className="px-3 sm:px-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
              <CardTitle className="text-base sm:text-lg">Directory</CardTitle>
              {variant === 'embedded' ? (headerActions ?? inlineActionButtons) : null}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3 w-full lg:w-auto">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 shrink-0 text-slate-500" />
                <Input
                  placeholder="Search name, email, phone, specialty"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <Select
                value={clinicFilter}
                onValueChange={(value) => {
                  setClinicFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <div className="flex items-center gap-2">
                    <Filter className="h-3.5 w-3.5 shrink-0" />
                    <SelectValue placeholder="All clinics" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All clinics</SelectItem>
                  {clinics.map((clinic) => (
                    <SelectItem key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={activeFilter}
                onValueChange={(value: 'all' | 'active' | 'inactive') => {
                  setActiveFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="inactive">Inactive only</SelectItem>
                  <SelectItem value="all">Active &amp; inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={portalFilter}
                onValueChange={(value: 'all' | 'yes' | 'no') => {
                  setPortalFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any portal status</SelectItem>
                  <SelectItem value="yes">Has portal login</SelectItem>
                  <SelectItem value="no">No portal login</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={sortBy}
                onValueChange={(value: 'name' | 'referrals' | 'recent') => {
                  setSortBy(value);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Sort: Name (A–Z)</SelectItem>
                  <SelectItem value="referrals">Sort: Most referrals</SelectItem>
                  <SelectItem value="recent">Sort: Recent referral</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-3 sm:px-6">
          {doctorsQuery.isLoading ? (
            <div className="py-16 text-center text-sm text-slate-500">
              Loading referring dentists...
            </div>
          ) : doctorsQuery.isError ? (
            <div className="py-16 text-center text-sm text-red-600">
              Failed to load referring dentists.
              <div className="mt-2 text-xs text-slate-500">
                {doctorsQuery.error instanceof Error
                  ? doctorsQuery.error.message
                  : 'Unknown error'}
              </div>
            </div>
          ) : totalRecords === 0 ? (
            <div className="py-16 text-center space-y-3">
              <Users className="h-10 w-10 mx-auto text-slate-300" />
              <div>
                <h3 className="text-lg font-semibold text-slate-700">
                  No referring dentists yet
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {search || clinicFilter !== 'all' || activeFilter !== 'all' || portalFilter !== 'all'
                    ? 'No records match the current filters. Adjust the filters or add a new dentist.'
                    : 'Add your first referring dentist to start tracking the directory.'}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => setDialogState({ open: true, editing: null })}
              >
                <PlusCircle className="h-4 w-4 mr-1.5" />
                Add your first dentist
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dentist</TableHead>
                      <TableHead>Clinic</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Portal access</TableHead>
                      <TableHead>Referrals</TableHead>
                      <TableHead className="w-12 text-right">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedDoctors.map((doctor) => {
                      const dcrNumber = (doctor as any).dcr_number as string | null | undefined;
                      return (
                      <TableRow
                        key={doctor.id}
                        className={doctor.is_active ? undefined : 'opacity-60'}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setPatientsDialog({ open: true, doctor })}
                              className="text-left font-semibold text-slate-900 hover:text-blue-700 hover:underline"
                              title="View patients referred by this dentist"
                            >
                              {doctor.name}
                            </button>
                            {!doctor.is_active && (
                              <Badge
                                variant="secondary"
                                className="bg-slate-200 text-slate-600 hover:bg-slate-200"
                              >
                                Inactive
                              </Badge>
                            )}
                          </div>
                          {dcrNumber ? (
                            <div className="text-xs text-slate-500 mt-0.5">
                              DCR {dcrNumber}
                            </div>
                          ) : null}
                          {doctor.specialty ? (
                            <div className="text-xs text-slate-500 mt-0.5">
                              {doctor.specialty}
                            </div>
                          ) : null}
                          {doctor.is_twin_city_doctor ? (
                            <Badge
                              variant="outline"
                              className="mt-1 text-[10px] border-blue-300 text-blue-700"
                            >
                              Twin City doctor
                            </Badge>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {doctor.clinic ? (
                            <span className="text-sm text-slate-700">{doctor.clinic.name}</span>
                          ) : (
                            <span className="text-sm text-slate-400">No clinic</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {doctor.phone ? (
                            <a
                              href={`tel:${doctor.phone.replace(/\s+/g, '')}`}
                              className="block text-sm text-slate-700 hover:underline"
                              title="Call this number"
                            >
                              {doctor.phone}
                            </a>
                          ) : null}
                          {doctor.email ? (
                            <a
                              href={`mailto:${doctor.email}`}
                              className="block text-xs text-slate-500 hover:underline"
                              title="Send an email"
                            >
                              {doctor.email}
                            </a>
                          ) : null}
                          {!doctor.phone && !doctor.email ? (
                            <span className="text-sm text-slate-400">No contact details</span>
                          ) : null}
                        </TableCell>
                        <TableCell>{renderPortalStatus(doctor)}</TableCell>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => setPatientsDialog({ open: true, doctor })}
                            className="text-left"
                            title="View referred patients"
                          >
                            <div className="text-sm font-medium tabular-nums text-slate-800 hover:text-blue-700 hover:underline">
                              {doctor.plato_patient_count || doctor.total_referrals}
                            </div>
                            <div className="text-xs text-slate-500">
                              {doctor.plato_patient_count > 0
                                ? 'patients'
                                : doctor.last_referral_date
                                  ? `Last: ${formatLastReferral(doctor.last_referral_date)}`
                                  : 'Never'}
                            </div>
                          </button>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                aria-label={`Actions for ${doctor.name}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  setDialogState({ open: true, editing: doctor })
                                }
                              >
                                <Edit2 className="mr-2 h-4 w-4" />
                                Edit details
                              </DropdownMenuItem>
                              {doctor.portal_account ? (
                                <DropdownMenuItem onClick={() => openResetDialog(doctor)}>
                                  <KeyRound className="mr-2 h-4 w-4" />
                                  Reset portal password
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => openAccountDialog(doctor)}>
                                  <KeyRound className="mr-2 h-4 w-4" />
                                  Create portal login
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {doctor.is_active ? (
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-600"
                                  disabled={toggleActiveMutation.isPending}
                                  onClick={() =>
                                    toggleActiveMutation.mutate({
                                      id: doctor.id,
                                      active: false,
                                    })
                                  }
                                >
                                  <UserX className="mr-2 h-4 w-4" />
                                  Deactivate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  disabled={toggleActiveMutation.isPending}
                                  onClick={() =>
                                    toggleActiveMutation.mutate({
                                      id: doctor.id,
                                      active: true,
                                    })
                                  }
                                >
                                  <UserCheck className="mr-2 h-4 w-4" />
                                  Reactivate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 ? (
                <div className="flex items-center justify-between gap-2 pt-4">
                  <div className="text-xs text-slate-500">
                    Showing {(safePage - 1) * PAGE_SIZE + 1}–
                    {Math.min(safePage * PAGE_SIZE, totalRecords)} of {totalRecords}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={safePage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Badge variant="outline">
                      Page {safePage} / {totalPages}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={safePage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <AddReferringDentistDialog
        open={dialogState.open}
        editingDoctor={dialogState.editing}
        onOpenChange={(open) =>
          setDialogState((prev) => ({ ...prev, open, editing: open ? prev.editing : null }))
        }
      />

      {/* Create a referring-dentist portal login for a directory doctor. */}
      <Dialog
        open={accountDialog.open}
        onOpenChange={(open) =>
          setAccountDialog((prev) => ({ ...prev, open, doctor: open ? prev.doctor : null }))
        }
      >
        <DialogContent className="max-w-md">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!accountDialog.doctor) return;
              createAccountMutation.mutate({
                doctor: accountDialog.doctor,
                email: accountEmail,
                password: accountPassword,
              });
            }}
          >
            <DialogHeader>
              <DialogTitle>Create portal account</DialogTitle>
              <DialogDescription>
                Give {accountDialog.doctor?.name ?? 'this dentist'} a login to the
                referring-dentist portal to track their referrals. Share the email and
                temporary password with them; they can change it after first login.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="portal-email">Login email</Label>
                <Input
                  id="portal-email"
                  type="email"
                  required
                  value={accountEmail}
                  onChange={(e) => setAccountEmail(e.target.value)}
                  placeholder="dentist@clinic.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="portal-password">Temporary password</Label>
                <div className="flex gap-2">
                  <Input
                    id="portal-password"
                    required
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAccountPassword(generateTempPassword())}
                  >
                    Regenerate
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAccountDialog({ open: false, doctor: null })}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createAccountMutation.isPending}>
                {createAccountMutation.isPending ? 'Creating...' : 'Create account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset portal password — confirm + new temp password (same flow as the edit dialog). */}
      <AlertDialog
        open={resetDialog.open}
        onOpenChange={(open) =>
          setResetDialog((prev) => ({ ...prev, open, doctor: open ? prev.doctor : null }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset portal password?</AlertDialogTitle>
            <AlertDialogDescription>
              This sets a new temporary password for {resetDialog.doctor?.name ?? 'this dentist'}
              {resetDialog.doctor?.portal_account?.email
                ? ` (${resetDialog.doctor.portal_account.email})`
                : ''}
              . Their current password stops working immediately, and they'll be asked to set a
              new one at next login. Share this with them securely.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 py-1">
            <Input
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              aria-label="New temporary password"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => setResetPassword(generateTempPassword())}
            >
              Regenerate
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                const accountId = resetDialog.doctor?.portal_account?.id;
                if (!accountId) return;
                resetPasswordMutation.mutate({ accountId, password: resetPassword });
              }}
              disabled={resetPasswordMutation.isPending || resetPassword.trim().length < 4}
            >
              {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Standalone "Add clinic" dialog. */}
      <Dialog open={clinicDialogOpen} onOpenChange={setClinicDialogOpen}>
        <DialogContent className="max-w-md">
          <form onSubmit={handleSubmitClinic}>
            <DialogHeader>
              <DialogTitle>Add referring clinic</DialogTitle>
              <DialogDescription>
                Clinics group multiple referring dentists together for easier filtering.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <div className="space-y-1.5">
                <Label htmlFor="clinic-name">
                  Clinic name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="clinic-name"
                  value={clinicForm.name}
                  onChange={(e) =>
                    setClinicForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Smile Dental @ Tampines"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="clinic-email">Email</Label>
                  <Input
                    id="clinic-email"
                    type="email"
                    value={clinicForm.email}
                    onChange={(e) =>
                      setClinicForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="clinic-phone">Phone</Label>
                  <Input
                    id="clinic-phone"
                    value={clinicForm.phone}
                    onChange={(e) =>
                      setClinicForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="clinic-address">Address</Label>
                <Input
                  id="clinic-address"
                  value={clinicForm.address}
                  onChange={(e) =>
                    setClinicForm((prev) => ({ ...prev, address: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="clinic-notes">Notes</Label>
                <Input
                  id="clinic-notes"
                  value={clinicForm.notes}
                  onChange={(e) =>
                    setClinicForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Internal notes about this clinic (optional)"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setClinicDialogOpen(false)}
                disabled={createClinicMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createClinicMutation.isPending}>
                {createClinicMutation.isPending ? 'Saving...' : 'Add clinic'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ReferringDoctorPatientsDialog
        doctor={patientsDialog.doctor}
        open={patientsDialog.open}
        onOpenChange={(open) =>
          setPatientsDialog((prev) => ({ open, doctor: open ? prev.doctor : null }))
        }
      />
    </div>
  );
};

export default ReferringDentistsTable;
